import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera } from "lucide-react";

export function VisitorForm({ form, setField, masters, onSelectOrOther, onOpenCamera }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Visitor Name *</Label>
        <Input value={form.visitor_name || ""} onChange={e => setField("visitor_name", e.target.value)} className="h-11" data-testid="field-visitor-name" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Mobile No. *</Label>
        <Input value={form.mobile_no || ""} onChange={e => setField("mobile_no", e.target.value)} className="h-11" data-testid="field-mobile-no" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Company / From</Label>
        <Input value={form.company_from || ""} onChange={e => setField("company_from", e.target.value)} className="h-11" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Purpose of Visit</Label>
        <Select value={form.purpose || ""} onValueChange={v => onSelectOrOther("purpose", v, "purposes")}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Select purpose" /></SelectTrigger>
          <SelectContent>
            {masters.purposes.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Whom to Meet</Label>
        <Select value={form.whom_to_meet || ""} onValueChange={v => onSelectOrOther("whom_to_meet", v, "employees")}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Select person" /></SelectTrigger>
          <SelectContent>
            {masters.employees.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Division / Section</Label>
        <Select value={form.division || ""} onValueChange={v => onSelectOrOther("division", v, "divisions")}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Select division" /></SelectTrigger>
          <SelectContent>
            {masters.divisions.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Visit Area *</Label>
        <Select value={form.visit_area || ""} onValueChange={v => setField("visit_area", v)}>
          <SelectTrigger className="h-11" data-testid="field-visit-area"><SelectValue placeholder="Select area" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Plant">Plant</SelectItem>
            <SelectItem value="Admin Block">Admin Block</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">No. of Persons</Label>
        <Input type="number" value={form.no_of_persons || ""} onChange={e => setField("no_of_persons", e.target.value)} className="h-11" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No.</Label>
        <Input value={form.vehicle_no || ""} onChange={e => setField("vehicle_no", e.target.value)} className="h-11" />
      </div>
      <div className="col-span-2 space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Material Carried</Label>
        <Input value={form.material_carried || ""} onChange={e => setField("material_carried", e.target.value)} className="h-11" />
      </div>
      <div className="col-span-2">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Photo (Optional)</Label>
        <div className="mt-1.5 flex items-center gap-3">
          {form.photo ? (
            <img src={form.photo} alt="Visitor" className="w-20 h-20 rounded border object-cover" />
          ) : (
            <div className="w-20 h-20 rounded border-2 border-dashed flex items-center justify-center text-gray-300">
              <Camera size={24} />
            </div>
          )}
          <Button variant="outline" size="sm" onClick={onOpenCamera} data-testid="open-camera-btn">
            <Camera size={14} className="mr-1" /> {form.photo ? "Retake" : "Capture Photo"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function EmployeeForm({ form, setField, masters, onSelectOrOther, currentLocation }) {
  // Auto-exclude the current location from destination options
  const allLocations = ["Focal Point", "Sahnewal"];
  const destinationOptions = allLocations.filter(l => l !== currentLocation);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Employee Name *</Label>
        <Select value={form.employee_name || ""} onValueChange={v => onSelectOrOther("employee_name", v, "employees")}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Select employee" /></SelectTrigger>
          <SelectContent>
            {masters.employees.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Department</Label>
        <Select value={form.department || ""} onValueChange={v => onSelectOrOther("department", v, "divisions")}>
          <SelectTrigger className="h-11"><SelectValue placeholder="Select department" /></SelectTrigger>
          <SelectContent>
            {masters.divisions.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
            <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Purpose</Label>
        <Input value={form.purpose || ""} onChange={e => setField("purpose", e.target.value)} className="h-11" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Destination *</Label>
        <Select value={form.destination || ""} onValueChange={v => setField("destination", v)} data-testid="field-destination">
          <SelectTrigger className="h-11" data-testid="field-destination"><SelectValue placeholder="Select destination" /></SelectTrigger>
          <SelectContent>
            {destinationOptions.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {form.destination === "Other" && (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Specify Destination</Label>
          <Input value={form.destination_other || ""} onChange={e => setField("destination_other", e.target.value)} className="h-11" placeholder="Enter destination" data-testid="field-destination-other" />
        </div>
      )}
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Expected Return Time</Label>
        <Input type="datetime-local" value={form.expected_return || ""} onChange={e => setField("expected_return", e.target.value)} className="h-11" />
      </div>
      <div className="space-y-1.5 flex items-end">
        <label className="flex items-center gap-2 cursor-pointer h-11">
          <input type="checkbox" checked={form.early_departure || false} onChange={e => setField("early_departure", e.target.checked)} className="w-5 h-5 rounded" />
          <span className="text-sm">Early Departure</span>
        </label>
      </div>
    </div>
  );
}
